import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const load = () => api.get("/trading/logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const errors = logs.filter((l) => l.status === "error").length;

  return (
    <Layout>
      <PageHeader title="Execution Logs" subtitle="Audit trail of every order attempt — success & errors"
        right={
          <button data-testid="refresh-logs" onClick={load}
            className="h-9 px-4 text-sm font-semibold border border-slate-300 flex items-center gap-2 hover:border-slate-900 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        } />
      <div className="min-w-0 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 text-sm">
          <span className="px-3 py-1 bg-slate-100 font-mono">Total: {logs.length}</span>
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 font-mono">Success: {logs.length - errors}</span>
          <span className="px-3 py-1 bg-rose-50 text-rose-700 font-mono">Errors: {errors}</span>
        </div>
        {logs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-16 text-center text-slate-400">No execution logs yet. Fire a signal to generate an audit trail.</div>
        ) : (
          <div className="w-full max-w-full min-w-0 overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full min-w-[850px] text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase text-slate-500 border-b border-slate-200">
                <th className="px-3 sm:px-6 py-3 whitespace-nowrap">Time</th><th className="px-3 sm:px-6 py-3 whitespace-nowrap">Status</th>
                <th className="px-3 sm:px-6 py-3 whitespace-nowrap">Symbol</th><th className="px-3 sm:px-6 py-3 whitespace-nowrap">Side</th>
                <th className="px-6 py-3 text-right">Qty</th><th className="px-3 sm:px-6 py-3 whitespace-nowrap">Broker</th>
                <th className="px-3 sm:px-6 py-3 whitespace-nowrap">Detail</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`log-${l.id}`}>
                    <td className="px-3 sm:px-6 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                    <td className="px-3 sm:px-6 py-2.5">
                      {l.status === "success"
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />SUCCESS</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700"><XCircle className="w-3.5 h-3.5" />ERROR</span>}
                    </td>
                    <td className="px-3 sm:px-6 py-2.5 font-mono whitespace-nowrap">{l.symbol}</td>
                    <td className={`px-3 sm:px-6 py-2.5 text-xs font-semibold whitespace-nowrap ${l.side === "BUY" ? "text-emerald-600" : "text-rose-600"}`}>{l.side}</td>
                    <td className="px-3 sm:px-6 py-2.5 text-right font-mono whitespace-nowrap">{l.qty}</td>
                    <td className="px-3 sm:px-6 py-2.5 text-xs uppercase text-slate-500 whitespace-nowrap">{l.broker} · {l.mode}</td>
                    <td className="px-3 sm:px-6 py-2.5 text-xs text-slate-500 whitespace-normal break-words align-top leading-relaxed min-w-[220px] max-w-[360px]">
                      {l.error || (l.response ? `id ${l.response.order_id} · ${l.response.routed}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
